import {Emitter} from './emitter';
import {PermissionStatus, SubscriptionStatus, TrackEvent, VideoQuality} from './enums';
import type {TrackPublishOptions} from './options';
import {
	type LocalAudioTrack,
	type LocalTrack,
	type LocalVideoTrack,
	type RemoteAudioTrack,
	type RemoteTrack,
	type RemoteVideoTrack,
	Track,
} from './track';

export abstract class TrackPublication extends Emitter<string> {
	kind: Track.Kind;
	trackName: string;
	trackSid: Track.SID;
	source: Track.Source;
	mimeType?: string;
	simulcasted?: boolean;
	dimensions?: Track.Dimensions;
	encryption = 0;
	protected _muted = false;

	protected constructor(kind: Track.Kind, trackSid: string, trackName: string, source: Track.Source) {
		super();
		this.kind = kind;
		this.trackSid = trackSid;
		this.trackName = trackName;
		this.source = source;
	}

	abstract get track(): Track | undefined;

	get isMuted(): boolean {
		return this.track?.isMuted ?? this._muted;
	}

	get isEnabled(): boolean {
		return true;
	}

	get isSubscribed(): boolean {
		return this.track !== undefined;
	}

	get isEncrypted(): boolean {
		return false;
	}

	get audioTrack(): Track | undefined {
		return this.kind === Track.Kind.Audio ? this.track : undefined;
	}

	get videoTrack(): Track | undefined {
		return this.kind === Track.Kind.Video ? this.track : undefined;
	}
}

export class LocalTrackPublication extends TrackPublication {
	#track?: LocalTrack;
	options?: TrackPublishOptions;

	constructor(track: LocalTrack, options?: TrackPublishOptions) {
		super(track.kind, track.sid ?? '', options?.name ?? track.source, options?.source ?? track.source);
		this.#track = track;
		this.options = options;
		this.dimensions = track.dimensions;
		const forward = (event: string) => () => this.emit(event, this);
		track.on(TrackEvent.Muted, forward(TrackEvent.Muted));
		track.on(TrackEvent.Unmuted, forward(TrackEvent.Unmuted));
	}

	override get track(): LocalTrack | undefined {
		return this.#track;
	}

	override get audioTrack(): LocalAudioTrack | undefined {
		return this.kind === Track.Kind.Audio ? (this.#track as LocalAudioTrack | undefined) : undefined;
	}

	override get videoTrack(): LocalVideoTrack | undefined {
		return this.kind === Track.Kind.Video ? (this.#track as LocalVideoTrack | undefined) : undefined;
	}

	/** @internal */
	clearTrack(): void {
		this.#track = undefined;
	}

	async mute(): Promise<LocalTrack | undefined> {
		await this.#track?.mute();
		return this.#track;
	}

	async unmute(): Promise<LocalTrack | undefined> {
		await this.#track?.unmute();
		return this.#track;
	}
}

/** What the MoQ subscriber does when the app changes a remote publication. */
export interface RemotePublicationControl {
	setSubscribed(subscribed: boolean): void;
	setVideoQuality(quality: VideoQuality): void;
	setVideoDimensions(dimensions: Track.Dimensions): void;
}

export class RemoteTrackPublication extends TrackPublication {
	#track?: RemoteTrack;
	#control?: RemotePublicationControl;
	#desired = true;
	#enabled = true;
	videoQuality: VideoQuality = VideoQuality.HIGH;
	permissionStatus: PermissionStatus = PermissionStatus.Allowed;

	constructor(
		kind: Track.Kind,
		trackSid: string,
		trackName: string,
		source: Track.Source,
		control?: RemotePublicationControl,
	) {
		super(kind, trackSid, trackName, source);
		this.#control = control;
	}

	override get track(): RemoteTrack | undefined {
		return this.#track;
	}

	override get audioTrack(): RemoteAudioTrack | undefined {
		return this.kind === Track.Kind.Audio ? (this.#track as RemoteAudioTrack | undefined) : undefined;
	}

	override get videoTrack(): RemoteVideoTrack | undefined {
		return this.kind === Track.Kind.Video ? (this.#track as RemoteVideoTrack | undefined) : undefined;
	}

	get isDesired(): boolean {
		return this.#desired;
	}

	/** Like LiveKit: subscribed means wanted by the app and a track present. */
	override get isSubscribed(): boolean {
		return this.#desired && this.#track !== undefined;
	}

	override get isEnabled(): boolean {
		return this.#enabled;
	}

	get subscriptionStatus(): SubscriptionStatus {
		if (!this.#desired) return SubscriptionStatus.Unsubscribed;
		return this.#track ? SubscriptionStatus.Subscribed : SubscriptionStatus.Desired;
	}

	/** @internal */
	setTrack(track: RemoteTrack | undefined): void {
		this.#track = track;
		if (track) track.setMuted(this._muted);
	}

	/** @internal */
	setMuted(muted: boolean): void {
		if (this._muted === muted) return;
		this._muted = muted;
		this.#track?.setMuted(muted);
		this.emit(muted ? TrackEvent.Muted : TrackEvent.Unmuted, this);
	}

	setSubscribed(subscribed: boolean): void {
		if (this.#desired === subscribed) return;
		this.#desired = subscribed;
		this.#control?.setSubscribed(subscribed);
		this.emit('subscriptionStatusChanged', this.subscriptionStatus);
	}

	setEnabled(enabled: boolean): void {
		if (this.#enabled === enabled) return;
		this.#enabled = enabled;
		this.#control?.setSubscribed(enabled && this.#desired);
	}

	setVideoQuality(quality: VideoQuality): void {
		this.videoQuality = quality;
		this.#control?.setVideoQuality(quality);
	}

	setVideoDimensions(dimensions: Track.Dimensions): void {
		this.#control?.setVideoDimensions(dimensions);
	}

	setVideoFPS(_fps: number): void {}
}
